import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { EngineIcon, engineLabels, getCompactDate, getDisplayDate, getDisplayTime } from 'Utils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import AppContext from 'Utils/AppContext';
import RunningJobRow from 'Utils/RunningJobRow';
import SortableTh, { sortRows } from 'Utils/SortableTh';
import { BRAND, STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import DiscoveryTooltip from 'DiscoveryTooltip';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';
import {
  latestInventoryOf,
  listDataSourceInventories,
  listMaskingJobGroups,
  rerunJobForRow,
  cancelMaskingJobById,
  cancelEncryptJob,
  cancelDecryptJob,
  inventoryContentOf,
  getMaskedInventorySummary,
} from 'Secure/api';

// Operation → short label + dot color (mockup 3a: mask violet, encrypt blue, decrypt orange).
const OP_META = {
  Masking: { label: 'Mask', color: BRAND.violet },
  Encryption: { label: 'Encrypt', color: BRAND.blue },
  Decryption: { label: 'Decrypt', color: STATUS.orange.text },
};

const statusStyle = (status) => {
  switch (status) {
    case 'MASKED':
    case 'ENCRYPTED':
      return STATUS.success;
    case 'DECRYPTED':
      return STATUS.orange;
    case 'RUNNING':
      return STATUS.info;
    case 'ERROR':
      return STATUS.danger;
    default:
      return STATUS.neutral;
  }
};

const cancelForOp = (op) => {
  if (op === 'Encryption') return cancelEncryptJob;
  if (op === 'Decryption') return cancelDecryptJob;
  return cancelMaskingJobById;
};

const fmt = (n) => Number(n || 0).toLocaleString();

const isEncryptionRow = (row) => row && (row.operation === 'Encryption' || row.operation === 'Decryption');

// The action button's label follows the row's current secured state: encrypted sources offer
// Decrypt, reverted (decrypted) sources offer re-Encrypt, masking rows just re-run.
const actionLabelFor = (row) => {
  if (isEncryptionRow(row)) {
    if (row.status === 'ENCRYPTED') return 'Decrypt';
    if (row.status === 'DECRYPTED') return 'Encrypt';
  }
  return '↻ Re-run';
};

const TOTAL_COLS = 9;

// Tables/columns protected for a row, from the inventory the job ran with (sensitive = protected).
const protectedCountsFor = (inventory) => {
  if (!inventory) return { tables: null, columns: null };
  const summary = getMaskedInventorySummary(inventoryContentOf(inventory));
  return { tables: summary.maskedTables, columns: summary.maskedFields };
};

/**
 * Secure "Job results" ledger — mockup 3a. One row per data source (most-recent secure job):
 * DATA SOURCE · TYPE · OPERATION · RECORDS · STATUS · LAST RUN · ACTIONS (Re-run + Inventory).
 * While a job is running the row is replaced by the shared RunningJobRow (name · engine · job
 * type · status · Cancel), matching the Data Discovery and Schema Metadata ledgers.
 */
const MaskingReportsTable = (props) => {
  const { connections, setNotification } = useContext(AppContext);
  const [rerunningId, setRerunningId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // dataSourceId → connection, so each row can show the source name + engine (the masking job
  // DTO carries neither). Same client-side join the previous MaterialTable used.
  const dataSourceById = useMemo(() => {
    const m = new Map();
    (connections || []).forEach((c) => {
      if (c && c.id != null) m.set(String(c.id), c);
    });
    return m;
  }, [connections]);

  // Inventories per source, for the tables/columns-protected counts.
  const [inventoriesBySource, setInventoriesBySource] = useState({});

  // Job groups render as their own ledger rows (latest run status); clicking one expands a
  // sub-list of the jobs inside the run. Refreshed alongside the parent's 30s job poll
  // (props.resultSets changes identity every poll).
  const [groups, setGroups] = useState([]);
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listMaskingJobGroups()
      .then((list) => {
        if (!cancelled) setGroups(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        console.log('Failed to load job groups for the ledger', e);
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [props.resultSets]);

  const inventoryForRow = (row) => {
    const bucket = row.dataSourceId != null ? inventoriesBySource[String(row.dataSourceId)] : null;
    if (!bucket) return null;
    if (row.inventoryId != null && bucket.byId.has(String(row.inventoryId))) {
      return bucket.byId.get(String(row.inventoryId));
    }
    return bucket.latest;
  };

  const sourceIdsKey = useMemo(() => {
    const ids = new Set();
    (props.resultSets || []).forEach((r) => {
      if (r && r.dataSourceId != null) ids.add(String(r.dataSourceId));
    });
    return Array.from(ids).sort().join(',');
  }, [props.resultSets]);

  useEffect(() => {
    if (!sourceIdsKey) return undefined;
    let cancelled = false;
    const ids = sourceIdsKey.split(',');
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const list = await listDataSourceInventories(id);
            const byId = new Map((list || []).filter(Boolean).map((inv) => [String(inv.id), inv]));
            return [id, { byId, latest: latestInventoryOf(list) }];
          } catch (e) {
            return [id, { byId: new Map(), latest: null }];
          }
        })
      );
      if (!cancelled) setInventoriesBySource(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceIdsKey]);

  // One-click re-run with the same inventory + provider. Encryption-family rows act on the row's
  // CURRENT state: ENCRYPTED offers decrypt, DECRYPTED offers re-encrypt; masking replays the job.
  // Stays ON this page: after the start call the job list reloads, the new RUNNING job becomes the
  // pair's most recent, and the row re-renders as the shared running-status row with its progress
  // bar — no navigation.
  const rerun = async (row) => {
    const { dataSourceId, operation, status, searchid } = row;
    if (dataSourceId == null || rerunningId != null) return;
    const op = operation || 'Masking';
    let effectiveOp = op;
    if (isEncryptionRow(row)) {
      if (status === 'ENCRYPTED') effectiveOp = 'Decryption';
      else if (status === 'DECRYPTED') effectiveOp = 'Encryption';
    }
    setRerunningId(searchid);
    try {
      await rerunJobForRow({ ...row, operation: effectiveOp });
      setNotification({
        header: `Running ${effectiveOp.toLowerCase()}`,
        body: `Started a new ${effectiveOp.toLowerCase()} job with the previous inventory and provider.`,
      });
      if (props.onReload) await props.onReload();
    } catch (e) {
      setNotification({ header: 'Re-run failed', body: e?.message || String(e) });
    } finally {
      setRerunningId(null);
    }
  };

  const cancel = async (row) => {
    if (row.jobId == null) return;
    setCancellingId(row.searchid);
    try {
      await cancelForOp(row.operation)(row.jobId);
      const ds = dataSourceById.get(String(row.dataSourceId));
      setNotification({
        header: 'Job cancelled',
        body: `Cancelled the ${(row.operation || 'masking').toLowerCase()} job for ${ds?.name || `source ${row.dataSourceId}`}.`,
      });
      if (props.onReload) await props.onReload();
    } catch (e) {
      setNotification({ header: 'Cancel failed', body: e?.message || String(e) });
    } finally {
      setCancellingId(null);
    }
  };

  const resultsLinkFor = (row) => {
    if (row.jobId == null) return null;
    if (row.operation === 'Encryption') return `/secure/encrypt/results/${encodeURIComponent(row.jobId)}`;
    if (row.operation === 'Decryption') return `/secure/decrypt/results/${encodeURIComponent(row.jobId)}`;
    if (row.status === 'MASKED' || row.status === 'ERROR') return `/secure/mask/results/${encodeURIComponent(row.jobId)}`;
    return null;
  };

  // Data-source names link to the source's risk summary page (the hub), not the
  // legacy mask/encrypt summary views.
  const riskProfileLink = (dataSourceId) => `/landscape/riskprofile/${encodeURIComponent(dataSourceId)}`;
  const nameLinkFor = (row) => riskProfileLink(row.dataSourceId);

  // Header-click sort; null = the ledger's incoming order. Sorts only the per-source job
  // rows — orchestration-group rows keep their own block above them.
  // Opens newest-run-first, which is what the leftmost (date) column sorts on.
  const [ledgerSort, setLedgerSort] = useState({ key: 'lastRun', dir: 'desc' });

  // Flat ledger: one row per source (the connection-parent = most recent job); drop child rows.
  const ledgerAccessors = {
    name: (r) => dataSourceById.get(String(r.dataSourceId))?.name || `#${r.dataSourceId}`,
    type: (r) => {
      const engine = dataSourceById.get(String(r.dataSourceId))?.type;
      return engine ? engineLabels[engine] || engine : null;
    },
    operation: (r) => (OP_META[r.operation] || {}).label || r.operation,
    records: (r) => (r.recordsProcessed != null ? Number(r.recordsProcessed) : null),
    itemsProtected: (r) => protectedCountsFor(inventoryForRow(r)).tables,
    columnsProtected: (r) => protectedCountsFor(inventoryForRow(r)).columns,
    status: (r) => r.status,
    lastRun: (r) => (r.date ? Date.parse(r.date) || null : null),
  };
  const rows = sortRows(
    (props.resultSets || []).filter((r) => r && !r.parent_connection && r.dataSourceId != null),
    ledgerSort,
    ledgerAccessors
  );

  const runPillFor = (run) => {
    switch (run && run.status) {
      case 'SUCCEEDED':
        return STATUS.success;
      case 'RUNNING':
        return STATUS.info;
      case 'FAILED':
        return STATUS.danger;
      default:
        return STATUS.neutral;
    }
  };

  const stepPillFor = (status) => {
    switch (status) {
      case 'SUCCEEDED':
        return STATUS.success;
      case 'RUNNING':
      case 'PENDING':
        return STATUS.info;
      case 'FAILED':
        return STATUS.danger;
      default:
        return STATUS.neutral;
    }
  };

  // Pair a group's definition steps with its latest run's steps (by stepOrder), then merge
  // consecutive steps sharing (source, inventory) with cross-source targets into ONE sub-row —
  // the runner executes them as a single fan-out job (one read+mask pass, one job id), so the
  // sub-list shows them as the single job they are.
  const groupSubRows = (group) => {
    const runSteps = new Map(
      ((group.latestRun && group.latestRun.steps) || []).map((s) => [s.stepOrder, s])
    );
    const paired = (group.steps || []).map((step) => ({ step, runStep: runSteps.get(step.stepOrder) || null }));
    const merged = [];
    let i = 0;
    while (i < paired.length) {
      const first = paired[i];
      const block = [first];
      if (first.step.targetDataSourceId != null) {
        let j = i + 1;
        while (
          j < paired.length &&
          paired[j].step.targetDataSourceId != null &&
          paired[j].step.dataSourceId === first.step.dataSourceId &&
          paired[j].step.inventoryId === first.step.inventoryId
        ) {
          block.push(paired[j]);
          j += 1;
        }
      }
      merged.push({
        step: first.step,
        runStep: first.runStep,
        targets: block
          .filter(({ step }) => step.targetDataSourceId != null)
          .map(({ step }) => ({ id: step.targetDataSourceId, name: step.targetDataSourceName })),
      });
      i += block.length;
    }
    return merged;
  };

  return (
    <Card data-testid='masking-reports-table'>
      <Card.Body>
        {/* textAlign left beats the global `.App { text-align: center }` inherit. */}
        <table className='w-100' style={{ fontSize: 13, textAlign: 'left' }}>
          <thead>
            <tr className='sg-eyebrow' style={{ fontSize: 11, color: 'var(--ink-400)', textAlign: 'left' }}>
              <SortableTh label='Last run' sortKey='lastRun' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Data source' sortKey='name' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Type' sortKey='type' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Operation' sortKey='operation' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Records' sortKey='records' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Items protected' sortKey='itemsProtected' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Columns protected' sortKey='columnsProtected' sort={ledgerSort} onSort={setLedgerSort} />
              <SortableTh label='Status' sortKey='status' sort={ledgerSort} onSort={setLedgerSort} />
              <th style={{ padding: '8px 10px' }} />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const run = group.latestRun || null;
              const pill = runPillFor(run);
              const expanded = expandedGroupId === group.id;
              const dateMs = run && (run.finishedAt || run.startedAt) ? Date.parse(run.finishedAt || run.startedAt) : null;
              // Merged sub-rows: a fan-out (one job, N targets) counts as ONE job here.
              const subRows = groupSubRows(group);
              return (
                <React.Fragment key={`group-${group.id}`}>
                  <tr
                    style={{ borderTop: '1px solid var(--ink-100)', cursor: 'pointer' }}
                    onClick={() => setExpandedGroupId(expanded ? null : group.id)}
                    data-testid={`secure-group-row-${group.id}`}
                    aria-expanded={expanded}
                  >
                    <td
                      style={{ padding: '9px 10px', whiteSpace: 'nowrap', fontFamily: FONT_MONO, color: 'var(--ink-500)' }}
                      className='sg-small'
                      title={dateMs ? `${getDisplayDate(dateMs)} ${getDisplayTime(dateMs)}` : undefined}
                    >
                      {dateMs ? getCompactDate(dateMs) : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', maxWidth: 320 }}>
                      <span className='sg-small' style={{ color: 'var(--ink-400)', marginRight: 6 }}>
                        {expanded ? '▾' : '▸'}
                      </span>
                      <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}>{group.name}</span>
                      <span
                        className='sg-small'
                        style={{
                          marginLeft: 8,
                          color: 'var(--ink-400)',
                          background: 'var(--ink-50)',
                          border: '1px solid var(--ink-150)',
                          borderRadius: 6,
                          padding: '1px 7px',
                        }}
                      >
                        orchestration group
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', color: 'var(--ink-400)' }}>—</td>
                    <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <span className='d-inline-flex align-items-center' style={{ gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: INK[400], flex: 'none' }} />
                        Group · {subRows.length} job{subRows.length === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', color: 'var(--ink-400)' }}>—</td>
                    <td style={{ padding: '9px 10px', color: 'var(--ink-400)' }}>—</td>
                    <td style={{ padding: '9px 10px', color: 'var(--ink-400)' }}>—</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span className='sg-pill sg-pill--nodot' style={{ background: pill.bg, color: pill.text, fontSize: 10.5 }}>
                        {run ? run.status : 'IDLE'}
                      </span>
                    </td>

                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Link
                        to='/secure/groups'
                        className='btn btn-sm btn-outline-secondary'
                        onClick={(e) => e.stopPropagation()}
                      >
                        Manage →
                      </Link>
                    </td>
                  </tr>
                  {expanded &&
                    subRows.map(({ step, runStep, targets }) => {
                      const stepPill = stepPillFor(runStep && runStep.status);
                      const stepEngine = dataSourceById.get(String(step.dataSourceId))?.type;
                      // A step that never ran has no stamp of its own; the group row above carries
                      // the run's date in the same column.
                      const stepDateMs = runStep && (runStep.finishedAt || runStep.startedAt)
                        ? Date.parse(runStep.finishedAt || runStep.startedAt) || null
                        : null;
                      const jobResultsTo =
                        runStep && runStep.orchestrationJobId != null &&
                        (runStep.status === 'SUCCEEDED' || runStep.status === 'FAILED')
                          ? `/secure/mask/results/${encodeURIComponent(runStep.orchestrationJobId)}`
                          : null;
                      return (
                        <tr
                          key={`group-${group.id}-step-${step.stepOrder}`}
                          style={{ background: 'var(--ink-50)', borderTop: '1px solid var(--ink-100)' }}
                          data-testid={`secure-group-${group.id}-step-${step.stepOrder}`}
                        >
                          <td
                            className='sg-small'
                            style={{ padding: '7px 10px', whiteSpace: 'nowrap', fontFamily: FONT_MONO, color: 'var(--ink-500)', verticalAlign: 'top' }}
                            title={stepDateMs ? `${getDisplayDate(stepDateMs)} ${getDisplayTime(stepDateMs)}` : undefined}
                          >
                            {stepDateMs ? getCompactDate(stepDateMs) : '—'}
                          </td>
                          <td style={{ padding: '7px 10px 7px 20px', fontFamily: FONT_MONO, fontSize: 12.5, verticalAlign: 'top' }}>
                            {/* source → first target, additional targets stacked below it. */}
                            <span className='d-inline-flex align-items-start' style={{ gap: 6 }}>
                              <Link to={riskProfileLink(step.dataSourceId)} style={{ color: BRAND.blue }}>
                                {step.dataSourceName || `#${step.dataSourceId}`}
                              </Link>
                              {targets.length > 0 ? (
                                <>
                                  <span style={{ color: 'var(--ink-400)' }}>→</span>
                                  <span className='d-inline-flex flex-column'>
                                    {targets.map((t) => (
                                      <Link key={t.id} to={riskProfileLink(t.id)} style={{ color: BRAND.blue }}>
                                        {t.name || `#${t.id}`}
                                      </Link>
                                    ))}
                                  </span>
                                </>
                              ) : (
                                <span className='sg-small' style={{ color: 'var(--ink-500)' }}>— in place</span>
                              )}
                            </span>
                          </td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            {stepEngine ? (
                              <span>
                                <EngineIcon engine={stepEngine} /> {engineLabels[stepEngine] || stepEngine}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--ink-400)' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <span className='d-inline-flex align-items-center' style={{ gap: 7 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: OP_META.Masking.color, flex: 'none' }} />
                              {`${OP_META.Masking.label} (${targets.length > 0 ? 'Distributed' : 'In Place'})`}
                              {targets.length > 1 && (
                                <span className='sg-small' style={{ color: 'var(--ink-500)' }}>· fan-out</span>
                              )}
                            </span>
                          </td>
                          {/* Spans the records / items / columns columns, less the one the leading
                              date cell now occupies — the row still totals TOTAL_COLS. */}
                          <td colSpan={2} className='sg-small' style={{ padding: '7px 10px', color: 'var(--ink-500)', verticalAlign: 'top' }}>
                            {step.inventoryName || (step.inventoryId != null ? `inventory #${step.inventoryId}` : '—')}
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <span
                              className='sg-pill sg-pill--nodot'
                              style={{ background: stepPill.bg, color: stepPill.text, fontSize: 10 }}
                            >
                              {runStep ? runStep.status : 'NOT RUN'}
                            </span>
                          </td>
                          <td className='sg-small' style={{ padding: '7px 10px', color: 'var(--ink-500)' }}>
                            {runStep && runStep.durationMs != null ? `${Math.round(runStep.durationMs / 1000)}s` : '—'}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {jobResultsTo ? (
                              <Link
                                to={jobResultsTo}
                                className='btn btn-sm'
                                style={{ background: BRAND.blue, borderColor: BRAND.blue, color: '#fff' }}
                                data-testid={`secure-group-${group.id}-step-${step.stepOrder}-results`}
                              >
                                Results →
                              </Link>
                            ) : (
                              <Button size='sm' disabled variant='outline-secondary' title='No results yet'>
                                Results →
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
            {rows.map((row) => {
              const ds = dataSourceById.get(String(row.dataSourceId));
              const name = ds?.name || `#${row.dataSourceId}`;
              const engine = ds?.type;
              const op = OP_META[row.operation] || { label: row.operation || 'Mask', color: INK[400] };
              // Cross-source job: the TARGET(S) are what got masked. A fan-out job is ONE job
              // with several destinations — render them all on its single row.
              const targetIds = Array.isArray(row.targetDataSourceIds)
                ? row.targetDataSourceIds
                : row.targetDataSourceId != null
                  ? [row.targetDataSourceId]
                  : [];
              // In-place = masked/secured rows written back into the source; Distributed = written
              // to separate target data source(s) (incl. fan-out). The suffix rides the op label.
              const opLabel = `${op.label} (${targetIds.length > 0 ? 'Distributed' : 'In Place'})`;
              // source → first target on one line, additional targets stacked below (one taller row).
              const targetLinks = targetIds.length > 0 && (
                <span
                  className='d-inline-flex align-items-start'
                  style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)', gap: 6, marginLeft: 6, verticalAlign: 'top' }}
                  title={targetIds.length > 1 ? 'Fan-out: one job wrote masked rows to every destination' : 'Cross-source mask: masked rows were written to this target'}
                  data-testid={`secure-row-target-${row.dataSourceId}`}
                >
                  <span style={{ color: 'var(--ink-400)' }}>→</span>
                  <span className='d-inline-flex flex-column'>
                    {targetIds.map((tid) => (
                      <Link key={tid} to={riskProfileLink(tid)} style={{ fontFamily: FONT_MONO, color: BRAND.blue }}>
                        {dataSourceById.get(String(tid))?.name || `#${tid}`}
                      </Link>
                    ))}
                  </span>
                </span>
              );

              if (row.status === 'RUNNING') {
                // Prefer table-level progress (matches the other ledgers); fall back to records
                // for engines that only report row counts.
                const progress = row.progress || null;
                const bar =
                  progress?.tablesTotal > 0
                    ? { completed: progress.tablesCompleted ?? 0, total: progress.tablesTotal, unit: getSourceNomenclature(ds?.type, ds?.datasourceType).item }
                    : progress?.recordsTotal > 0
                    ? { completed: progress.recordsCompleted ?? 0, total: progress.recordsTotal, unit: 'records' }
                    : {};
                return (
                  <RunningJobRow
                    key={row.searchid}
                    testId={`secure-running-${row.dataSourceId}`}
                    nameNode={
                      <span className='d-inline-flex align-items-start'>
                        <Link to={nameLinkFor(row)} style={{ fontFamily: FONT_MONO, color: BRAND.blue }}>
                          {name}
                        </Link>
                        {targetLinks}
                      </span>
                    }
                    leading={
                      <span className='sg-small' style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
                        {row.date ? getCompactDate(row.date) : '—'}
                      </span>
                    }
                    engine={engine}
                    jobType={opLabel}
                    status='RUNNING'
                    totalCols={TOTAL_COLS}
                    {...bar}
                    cancelling={cancellingId === row.searchid}
                    onCancel={() => cancel(row)}
                  />
                );
              }

              const pill = statusStyle(row.status);
              const protectedCounts = protectedCountsFor(inventoryForRow(row));
              const resultsTo = resultsLinkFor(row);
              const dateLabel = row.date ? getCompactDate(row.date) : '—';
              return (
                <tr key={row.searchid} style={{ borderTop: '1px solid var(--ink-100)' }}>
                  <td
                    style={{ padding: '9px 10px', whiteSpace: 'nowrap', fontFamily: FONT_MONO, color: 'var(--ink-500)', verticalAlign: 'top' }}
                    className='sg-small'
                    title={row.date ? `${getDisplayDate(row.date)} ${getDisplayTime(row.date)}` : undefined}
                  >
                    {resultsTo ? (
                      <DiscoveryTooltip text='View results' placement='top'>
                        <Link to={resultsTo} style={{ color: 'var(--ink-700)' }}>{dateLabel}</Link>
                      </DiscoveryTooltip>
                    ) : (
                      dateLabel
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', maxWidth: 320, verticalAlign: 'top' }}>
                    <span className='d-inline-flex align-items-start'>
                      <Link to={nameLinkFor(row)} style={{ fontFamily: FONT_MONO, color: BRAND.blue }}>
                        {name}
                      </Link>
                      <AdoptedFromBadge
                        registeredServiceId={ds?.registeredServiceId}
                        registeredServiceName={ds?.registeredServiceName}
                      />
                      {targetLinks}
                    </span>
                  </td>
                  <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                    {engine ? (
                      <span>
                        <EngineIcon engine={engine} /> {engineLabels[engine] || engine}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ink-400)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                    <span className='d-inline-flex align-items-center' style={{ gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: op.color, flex: 'none' }} />
                      {opLabel}
                    </span>
                  </td>
                  <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                    {row.recordsProcessed ? fmt(row.recordsProcessed) : '—'}
                  </td>
                  <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                    {protectedCounts.tables != null ? fmt(protectedCounts.tables) : '—'}
                  </td>
                  <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                    {protectedCounts.columns != null ? fmt(protectedCounts.columns) : '—'}
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    {row.status === 'ERROR' && row.errorMessage ? (
                      <DiscoveryTooltip text={String(row.errorMessage)} placement='top'>
                        <span className='sg-pill sg-pill--nodot' style={{ background: pill.bg, color: pill.text, fontSize: 10.5 }}>
                          {row.status}
                        </span>
                      </DiscoveryTooltip>
                    ) : (
                      <span className='sg-pill sg-pill--nodot' style={{ background: pill.bg, color: pill.text, fontSize: 10.5 }}>
                        {row.status || '—'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size='sm'
                      variant='outline-secondary'
                      className='mr-2'
                      title={
                        isEncryptionRow(row)
                          ? 'Reverse this operation using the same inventory and provider'
                          : 'Re-run with the previous inventory and provider'
                      }
                      disabled={rerunningId != null}
                      onClick={() => rerun(row)}
                      data-testid={`secure-rerun-${row.searchid}`}
                    >
                      {rerunningId === row.searchid ? 'Starting…' : actionLabelFor(row)}
                    </Button>
                    {resultsTo ? (
                      <Link
                        to={resultsTo}
                        className='btn btn-sm'
                        style={{ background: BRAND.blue, borderColor: BRAND.blue, color: '#fff' }}
                        data-testid={`secure-results-${row.searchid}`}
                      >
                        Results →
                      </Link>
                    ) : (
                      <Button size='sm' disabled variant='outline-secondary' title='No results yet'>
                        Results →
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && groups.length === 0 && (
              <tr>
                <td colSpan={TOTAL_COLS} className='text-center sg-small' style={{ padding: 24, color: 'var(--ink-400)' }}>
                  {props.fetchingData ? 'Loading…' : 'No secured jobs yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
};

export default MaskingReportsTable;
